import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useProfile } from '../hooks/useProfile';
import { supabase } from '../lib/supabase';
import { useTranslation } from 'react-i18next';
import { Title } from '../components/ui/Title';
import { MusicianFields } from '../components/forms/MusicianFields';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { LocationSearch } from '../components/LocationSearch';
import { MUSICIAN_STATUSES } from '../constants/music';
import { useMusicTranslations } from '../utils/translations';
import { Trash2, User } from 'lucide-react';
import { optimizeImage, validateImageFile } from '../utils/imageOptimizer';

const editProfileSchema = z.object({
  artist_name: z.string().min(2).max(50),
  full_name: z.string().min(2).max(100),
  description: z.string().max(500).optional(),
  location: z.string().min(2, 'Location must be at least 2 characters').max(100, 'Location cannot exceed 100 characters'),
  coordinates: z.object({
    latitude: z.number(),
    longitude: z.number(),
  }).nullable(),
  genres: z.array(z.string()).optional(),
  skills: z.array(z.string()).optional(),
  gender: z.enum(['male', 'female', 'diverse']).nullable(),
  status: z.string().min(1, 'Please select a status'),
});

type EditProfileFormData = z.infer<typeof editProfileSchema>;

function EditProfile() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { profile, loading, error, updateProfile, refreshProfile } = useProfile();
  const { getTranslatedMusicianStatuses } = useMusicTranslations();
  const [updateError, setUpdateError] = React.useState('');
  const [uploading, setUploading] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const { register, handleSubmit, formState: { errors }, setValue, watch, reset } = useForm<EditProfileFormData>({
    resolver: zodResolver(editProfileSchema),
  });

  useEffect(() => {
    if (profile) {
      reset({
        artist_name: profile.artist_name || '',
        full_name: profile.full_name || '',
        description: profile.description || '',
        location: profile.location || '',
        coordinates: profile.coordinates,
        genres: profile.genres || [],
        skills: profile.skills || [],
        gender: profile.gender,
        status: profile.status || 'Aktiv',
      });
    }
  }, [profile, reset]);

  const watchGenres = watch('genres', []);
  const watchSkills = watch('skills', []);

  const translatedMusicianStatuses = getTranslatedMusicianStatuses();

  const handleFileUpload = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      setUploading(true);
      setUpdateError('');

      let file = event.target.files?.[0];
      if (!file) return;

      await validateImageFile(file);

      // Optimize image before upload
      const optimized = await optimizeImage(file, {
        maxWidth: 500,
        maxHeight: 500,
        quality: 0.85,
        format: 'image/webp',
      });
      file = optimized.file;

      // Generate a simple alphanumeric filename
      const fileName = `${Date.now()}${Math.random().toString(36).slice(2)}.webp`;

      // Delete old avatar if exists
      if (profile?.avatar_url) {
        const oldFileName = profile.avatar_url.split('/').pop();
        if (oldFileName) {
          await supabase.storage
            .from('avatars')
            .remove([oldFileName]);
        }
      }

      // Upload new file
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(fileName, file, {
          cacheControl: '31536000',
          contentType: 'image/webp'
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(fileName);

      console.log('Avatar upload successful, updating profile with URL:', publicUrl);

      // Update profile with new avatar URL
      await updateProfile({ 
        avatar_url: publicUrl,
        updated_at: new Date().toISOString() // Force updated_at to change
      });
      
      console.log('Profile updated with new avatar');
      
      // Force refresh to ensure we have the latest data
      await refreshProfile();
    } catch (err) {
      console.error('Avatar upload error:', err);
      setUpdateError(err instanceof Error ? err.message : 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  }, [profile, updateProfile, refreshProfile]);

  const handleRemoveAvatar = async () => {
    try {
      setUploading(true);
      setUpdateError('');

      if (profile?.avatar_url) {
        const fileName = profile.avatar_url.split('/').pop();
        if (fileName) {
          await supabase.storage
            .from('avatars')
            .remove([fileName]);
        }
      }

      console.log('Removing avatar, updating profile');
      await updateProfile({ 
        avatar_url: null,
        updated_at: new Date().toISOString() // Force updated_at to change
      });
      
      // Force refresh to ensure we have the latest data
      await refreshProfile();
    } catch (err) {
      console.error('Remove avatar error:', err);
      setUpdateError(err instanceof Error ? err.message : 'Failed to remove avatar');
    } finally {
      setUploading(false);
    }
  };

  const handleGenreChange = (genre: string) => {
    const currentGenres = watchGenres || [];
    const newGenres = currentGenres.includes(genre)
      ? currentGenres.filter(g => g !== genre)
      : [...currentGenres, genre];
    setValue('genres', newGenres);
  };

  const handleSkillChange = (skill: string) => {
    const currentSkills = watchSkills || [];
    const newSkills = currentSkills.includes(skill)
      ? currentSkills.filter(s => s !== skill)
      : [...currentSkills, skill];
    setValue('skills', newSkills);
  };

  const handleLocationChange = (location: { name: string; coordinates: { latitude: number; longitude: number } }) => {
    setValue('location', location.name);
    setValue('coordinates', location.coordinates);
  };

  const onSubmit = async (data: EditProfileFormData) => {
    if (isSubmitting) return; // Prevent multiple submissions
    
    try {
      setIsSubmitting(true);
      setUpdateError('');
      console.log('Form submit: Updating profile with data:', data);
      
      // Always include updated_at to force a change
      await updateProfile({
        ...data,
        updated_at: new Date().toISOString()
      });
      
      console.log('Form submit: Profile updated successfully');
      
      // Force refresh to ensure we have the latest data
      await refreshProfile();
      
      navigate('/profile');
    } catch (err) {
      console.error('Form submit error:', err);
      setUpdateError(err instanceof Error ? err.message : 'Failed to update profile');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-6 pb-8 md:py-24">
        <div className="text-center text-light/60">{t('common.loading')}</div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="container mx-auto px-6 pb-8 md:py-24">
        <div className="text-center text-red-500">
          {error?.message || 'Profile not found'}
        </div>
      </div>
    );
  }

  const breadcrumbItems = [
    { label: t('profile.title'), href: '/profile' },
    { label: t('profile.editProfile') },
  ];

  return (
    <div className="container mx-auto px-6 pb-8 md:py-24">
      <div className="mb-6">
        <Breadcrumbs items={breadcrumbItems} />
      </div>

      <Title subtitle={t('forms.updateProfile')}>
        {t('profile.editProfile')}
      </Title>

      <div className="max-w-4xl mx-auto">
        {updateError && (
          <div className="bg-red-500/10 text-red-500 p-4 rounded-lg mb-6 text-center">
            {updateError}
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
          <div className="panel">
            <div className="mb-6 flex flex-col gap-4 sm:gap-2 sm:items-center sm:flex-row sm:space-x-4">
              <div className="w-20 h-20 rounded-full bg-gray-custom/10 flex items-center justify-center overflow-hidden">
                {profile.avatar_url ? (
                  <img
                    src={`${profile.avatar_url}?t=${Date.now()}`}
                    alt={profile.artist_name || ''}
                    className="w-full h-full object-cover"
                   loading="lazy" />
                ) : (
                  <User className="w-8 h-8 text-light/60" />
                )}
              </div>
              <div className="flex items-center">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileUpload}
                  accept="image/*"
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="btn-outline"
                >
                  {uploading ? t('forms.uploading') : t('forms.changeAvatar')}
                </button>
                {profile.avatar_url && (
                  <button
                    type="button"
                    onClick={handleRemoveAvatar}
                    disabled={uploading}
                    className="ml-2 btn-outline !border-red-500/20 !text-red-500 hover:!border-red-500/40"
                    title={t('forms.removeAvatar')}
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                )}
              </div>
            </div>

            <div className="space-y-6">
              <div>
                <label htmlFor="artist_name" className="block text-sm font-medium text-light/80">
                  {t('forms.artistName')}
                </label>
                <input
                  type="text"
                  id="artist_name"
                  {...register('artist_name')}
                  placeholder={t('forms.enterArtistName')}
                  className="mt-1 block w-full rounded-lg bg-dark border border-gray-custom/20 px-4 py-2 text-light focus:border-gray-custom/40 focus:outline-none"
                />
                {errors.artist_name && (
                  <p className="mt-1 text-sm text-red-500">{errors.artist_name.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="full_name" className="block text-sm font-medium text-light/80">
                  {t('forms.fullName')}
                </label>
                <input
                  type="text"
                  id="full_name"
                  {...register('full_name')}
                  placeholder={t('forms.enterFullName')}
                  className="mt-1 block w-full rounded-lg bg-dark border border-gray-custom/20 px-4 py-2 text-light focus:border-gray-custom/40 focus:outline-none"
                />
                {errors.full_name && (
                  <p className="mt-1 text-sm text-red-500">{errors.full_name.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="description" className="block text-sm font-medium text-light/80">
                  {t('forms.description')}
                </label>
                <textarea
                  id="description"
                  {...register('description')}
                  rows={4}
                  className="mt-1 block w-full rounded-lg bg-dark border border-gray-custom/20 px-4 py-2 text-light focus:border-gray-custom/40 focus:outline-none"
                  placeholder={t('forms.tellAboutYourself')}
                />
                {errors.description && (
                  <p className="mt-1 text-sm text-red-500">{errors.description.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-light/80">
                  {t('forms.gender')}
                </label>
                <div className="mt-2 flex flex-col sm:flex-row gap-2 sm:space-x-4">
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      {...register('gender')}
                      value="male"
                      className="form-radio text-primary focus:ring-primary"
                    />
                    <span className="ml-2 text-light/80">{t('forms.male')}</span>
                  </label>
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      {...register('gender')}
                      value="female"
                      className="form-radio text-primary focus:ring-primary"
                    />
                    <span className="ml-2 text-light/80">{t('forms.female')}</span>
                  </label>
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      {...register('gender')}
                      value="diverse"
                      className="form-radio text-primary focus:ring-primary"
                    />
                    <span className="ml-2 text-light/80">{t('forms.diverse')}</span>
                  </label>
                </div>
                {errors.gender && (
                  <p className="mt-1 text-sm text-red-500">{errors.gender.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-light/80 mb-2">
                  {t('forms.location')}
                </label>
                <LocationSearch
                  value={watch('location') || ''}
                  onChange={handleLocationChange}
                  error={errors.location?.message}
                />
              </div>
            </div>
            
            {/* Add link to become musician if not already a musician */}
            {!profile.is_musician && (
              <div className="mt-6 pt-6 border-t border-gray-custom/20 text-center">
                <Link
                  to="/become-musician"
                  className="text-light transition-colors underline"
                >
                  {t('profile.becomeMusician')}
                </Link>
              </div>
            )}
          </div>

          <div className="panel">
            <h3 className="text-xl font-semibold mb-6">Status</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {translatedMusicianStatuses.map((status) => (
                <label key={status.value} className="flex items-center space-x-2">
                  <input
                    type="radio"
                    value={status.value}
                    {...register('status')}
                    className="h-4 w-4 border-gray-custom/20 text-primary focus:ring-primary"
                  />
                  <span className="text-light/80">{status.label}</span>
                </label>
              ))}
            </div>
            {errors.status && (
              <p className="mt-4 text-sm text-red-500">{errors.status.message}</p>
            )}
          </div>

          {profile.is_musician && (
            <div className="panel">
              <h3 className="text-xl font-semibold mb-6">{t('forms.musicianProfile')}</h3>
              <MusicianFields
                genres={watchGenres}
                skills={watchSkills}
                onGenreChange={handleGenreChange}
                onSkillChange={handleSkillChange}
              />
            </div>
          )}

          <div className="flex flex-col md:flex-row justify-end md:space-x-4">
            <button
              type="button"
              onClick={() => navigate('/profile')}
              className="btn-outline order-2 mt-6 md:mt-0"
              disabled={isSubmitting}
            >
              {t('common.cancel')}
            </button>
            <button 
              type="submit" 
              className="btn-secondary md:order-2"
              disabled={isSubmitting}
            >
              {isSubmitting ? t('common.saving') : t('forms.saveChanges')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EditProfile;